import { cn } from '@/lib/utils';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { TruncatedText } from '@/components/common/truncated-text';
import { CopyClipboard } from '@/components/common/copy-clipboard';
import { Pagination } from '@/components/common/pagination';
import { LoadingOverlay } from '@/components/common/loading-overlay';
import { formatToFullDateTime } from '@/utils/date-formatter';
import { formatNumberWithCommas } from '@/utils/format-number-with-commas';
import { PushContentData } from '@/services/agent-v2-push-content-list';

interface AiChatTableProps {
  contentList: PushContentData[];
  loading: boolean;
  total: number;
  page: number;
  pageSize: number;
  changePage: (page: number) => void;
  search: () => void;
}

/** 智能体内容管理 - AI 对话表格 */
export const AiChatTable = ({
  contentList,
  loading,
  total,
  page,
  pageSize,
  changePage,
  search,
}: AiChatTableProps) => {
  const { t } = useTranslation();

  const statusMap: Record<number, string> = {
    0: t('agent_dashboard.content_management.search.new_status.processing'),
    1: t('agent_dashboard.content_management.search.new_status.finished'),
    2: t('agent_dashboard.content_management.search.new_status.failed'),
    3: t('agent_dashboard.content_management.search.new_status.cancelled'),
    9: t('agent_dashboard.content_management.search.new_status.deleted'),
    10: t('agent_dashboard.content_management.search.new_status.timeout'),
  };

  const handlePageChange = (nextPage: number) => {
    changePage(nextPage);
    search();
  };

  const modelMap: Record<string, string> = {
    "doubao-seed-2-0-pro-260215": "豆包 Seed 2.0 Pro",
  };

  return (
    <div className='relative flex-1 flex flex-col rounded-lg border overflow-hidden'>
      <div className="overflow-auto flex-1">
        <table className="w-full rounded-t-lg relative">
          <thead className='bg-gray-50 sticky top-0 border-b border-[rgba(234,236,240,1)] h-11 z-20'>
            <tr className="bg-gray-50">
              <Th>{t('agent_dashboard.content_management.table.ai_chat.message_id')}</Th>
              <Th>
                {t('agent_dashboard.content_management.table.user_nickname')}/
                {t('agent_dashboard.content_management.table.id')}
              </Th>
              <Th>{t('agent_dashboard.content_management.table.ai_chat.model')}</Th>
              <Th>{t('agent_dashboard.content_management.table.ai_chat.token_consumption')}</Th>
              <Th>{t('agent_dashboard.content_management.table.ai_chat.category')}</Th>
              <Th>{t('agent_dashboard.content_management.table.ai_chat.create_time')}</Th>
              <Th>{t('agent_dashboard.content_management.table.status')}</Th>
            </tr>
          </thead>
          <tbody className='bg-white'>
            {contentList?.length > 0 && contentList.map((item, index) => {
              const taskId = item.aiChatRecord?.taskId;
              const model = item.aiChatRecord?.model;
              const totalTokens = item.aiChatRecord?.usage?.totalTokens;

              return (
                <tr key={`${item.id}-${index}`} className='border-b h-18 transition-colors duration-200'>
                  <Td>
                    {taskId ? (
                      <div className='flex items-center'>
                        <TruncatedText text={taskId} className='mr-1' />
                        <CopyClipboard text={taskId} />
                      </div>
                    ) : '-'}
                  </Td>
                  <Td className='min-w-[8rem]'>
                    <div>
                      <TruncatedText text={item.userName} className='text-black pb-1' />
                      <div className='flex items-center'>
                        <TruncatedText text={item.userId.toString()} className='mr-1' />
                        <CopyClipboard text={item.userId.toString()} />
                      </div>
                    </div>
                  </Td>
                  <Td>
                    {model ? <TruncatedText text={modelMap[model] ?? model} /> : '-'}
                  </Td>
                  <Td>
                    {totalTokens != null
                      ? formatNumberWithCommas({ num: totalTokens, decimal: 0, type: 'en-US' })
                      : '-'}
                  </Td>
                  <Td>{t('agent_dashboard.content_management.search.type.ai_speech')}</Td>
                  <Td className='min-w-[7rem]'>
                    {item.createdAt ? formatToFullDateTime(item.createdAt * 1000) : '-'}
                  </Td>
                  <Td className='min-w-[8rem] flex-shrink-0'>
                    <div className={cn(
                      "flex max-w-max items-center py-1 px-2 rounded-full font-medium text-sm",
                      item.status === 0 && "text-[rgba(23,92,211,1)] bg-[rgba(239,248,255,1)]",
                      item.status === 9 && "text-[rgba(52,64,84,1)] bg-[rgba(242,244,247,1)]",
                      item.status === 1 && "text-[rgba(6,118,71,1)] bg-[rgba(236,253,243,1)]",
                      [2, 10].includes(item.status) && "text-[rgba(192,16,72,1)] bg-[rgba(255,241,243,1)]",
                      item.status === 3 && "text-[rgba(52,64,84,1)] bg-[rgba(242,244,247,1)]",
                    )}>
                      <span className='text-sm'>{statusMap[item.status] ?? item.status}</span>
                    </div>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className='h-[4.25rem] border-t border-[rgba(234,236,240,1)] px-6 flex items-center'>
        <Pagination
          total={total}
          page={page}
          pageSize={pageSize}
          onChange={handlePageChange}
        />
      </div>
      <LoadingOverlay loading={loading} />
    </div>
  );
};

const Th = ({ children, className }: { children?: React.ReactNode; className?: string }) => (
  <th className={cn(
    'min-w-[6.25rem] max-w-[12.5rem] pl-6 text-left text-sm font-semibold text-[rgba(16,24,40,1)]',
    className,
  )}>
    {children}
  </th>
);

const Td = ({ children, className }: { children?: React.ReactNode; className?: string }) => (
  <td className={cn(
    'min-w-[6.25rem] max-w-[12.5rem] pl-6 text-sm font-medium text-[rgba(71,84,103,1)]',
    className,
  )}>
    {children}
  </td>
);
